import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, of, switchMap } from 'rxjs';
import * as ChildDeactivationActions from './child-deactivation.actions';
import { ChildDeactivationService } from '@core/services/child-deactivation.service';
import { SchoolUserExemptionReason } from '@core/models/domain/account.domain';

/** Reduces the backend's FAILED status code, an HttpErrorResponse or an Error to one message. */
export function extractChildDeactivationErrorMessage(error: any): string {
  if (typeof error === 'string') {
    return error;
  }
  if (error?.status === 'FAILED' && error?.reason) {
    return error.reason;
  }
  if (error?.error?.statusCode?.reason) {
    return error.error.statusCode.reason;
  }
  if (error?.message) {
    return error.message;
  }
  return 'An unexpected error occurred. Please try again.';
}

@Injectable()
export class ChildDeactivationEffects {
  constructor(private actions$: Actions, private childDeactivationService: ChildDeactivationService) {}

  loadRequests$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChildDeactivationActions.loadRequests),
      switchMap(() =>
        this.childDeactivationService.loadPendingRequests().pipe(
          map((requests) => ChildDeactivationActions.loadRequestsSuccess({ requests })),
          catchError((error) => of(ChildDeactivationActions.loadRequestsFailure({
            error: extractChildDeactivationErrorMessage(error)
          })))
        )
      )
    )
  );

  loadResolvedRequests$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChildDeactivationActions.loadResolvedRequests),
      switchMap(() =>
        this.childDeactivationService.loadResolvedRequests().pipe(
          map((requests) => ChildDeactivationActions.loadResolvedRequestsSuccess({ requests })),
          catchError((error) => of(ChildDeactivationActions.loadResolvedRequestsFailure({
            error: extractChildDeactivationErrorMessage(error)
          })))
        )
      )
    )
  );

  approveRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChildDeactivationActions.approveRequest),
      mergeMap(({ requestId }) =>
        this.childDeactivationService.approve({ requestId }).pipe(
          map((id) => ChildDeactivationActions.approveRequestSuccess({ requestId: id })),
          catchError((error) => of(ChildDeactivationActions.approveRequestFailure({
            requestId,
            error: extractChildDeactivationErrorMessage(error)
          })))
        )
      )
    )
  );

  declineRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChildDeactivationActions.declineRequest),
      mergeMap(({ request }) =>
        this.childDeactivationService.decline(request).pipe(
          map((id) => ChildDeactivationActions.declineRequestSuccess({ requestId: id, reason: request.reason })),
          catchError((error) => of(ChildDeactivationActions.declineRequestFailure({
            requestId: request.requestId,
            error: extractChildDeactivationErrorMessage(error)
          })))
        )
      )
    )
  );

  exemptRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChildDeactivationActions.exemptRequest),
      mergeMap(({ request }) =>
        this.childDeactivationService.exempt(request).pipe(
          map((id) => ChildDeactivationActions.exemptRequestSuccess({
            requestId: id,
            exemptionReason: request.exemptionReason as SchoolUserExemptionReason,
            exemptionNote: request.exemptionNote
          })),
          catchError((error) => of(ChildDeactivationActions.exemptRequestFailure({
            requestId: request.requestId,
            error: extractChildDeactivationErrorMessage(error)
          })))
        )
      )
    )
  );
}
